import { Amplify } from 'aws-amplify';
import axios from 'axios';
import { logger } from './logger.js';
import id from '../Pages/User/tools/[id].js';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import 'aws-amplify/auth/enable-oauth-listener';
import {
  autoSignIn,
  confirmResetPassword,
  confirmSignUp as awsConfirmSignUp,
  fetchAuthSession,
  resendSignUpCode as awsResendSignUpCode,
  resetPassword,
  signIn as awsSignIn,
  signInWithRedirect as awsSignInWithRedirect,
  signOut as awsSignOut,
  signUp as awsSignUp,
  updatePassword
} from 'aws-amplify/auth';

import JSCookie from 'js-cookie';

const authConfig = {
  Cognito: {
    userPoolId: process.env.REACT_APP_USER_POOL,
    userPoolClientId: process.env.REACT_APP_CLIENT_ID,
    loginWith: {
      oauth: {
        domain: process.env.REACT_APP_USER_POOL_URI,
        scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
        redirectSignIn: [process.env.REACT_APP_DOMAIN],
        redirectSignOut: [process.env.REACT_APP_DOMAIN],
        responseType: 'code'
      }
    }
  }
};

class MyCustomStorage {
  constructor() {
    this.domain = process.env.NODE_ENV === 'development' ? 'localhost' : '.puredesire.org';
  }

  async setItem(key, value) {
    const split = key.split('.');
    const realKey = split[split.length - 1];
    JSCookie.set(`Cognito.${realKey}`, value, { domain: this.domain });
  }

  async getItem(key) {
    const split = key.split('.');
    const realKey = split[split.length - 1];
    return JSCookie.get(`Cognito.${realKey}`);
  }

  async removeItem(key) {
    const split = key.split('.');
    const realKey = split[split.length - 1];
    JSCookie.remove(`Cognito.${realKey}`, { domain: this.domain });
  }

  async clear() {
    JSCookie.get().forEach((cookie) => {
      if (cookie.startsWith('Cognito.')) {
        JSCookie.remove(`${cookie}`, { domain: this.domain });
      }
    });
  }
}

Amplify.configure({
  Auth: authConfig
});

cognitoUserPoolsTokenProvider.setKeyValueStorage(new MyCustomStorage());

async function signUp(email, password, name = '', role = 'user') {
  return await awsSignUp({
    username: email,
    password
  }).then(() => {
    try {
      const registerData = {
        email,
        role,
        name
      };
      axios.post(`${process.env.REACT_APP_API_URL}/auth/`, registerData);
      autoSignIn();
    } catch (err) {
      logger.error('Error signing up', err);
      return err;
    }
  });
}

async function signIn(email, password) {
  return await awsSignIn({ username: email, password });
}

async function confirmSignUp(email, code) {
  return await awsConfirmSignUp({ username: email, confirmationCode: code });
}

async function resendConfirmationCode(username) {
  return await awsResendSignUpCode({ username });
}

// pass in true to sign out from all devices
async function signOut() {
  return await awsSignOut();
}

async function federatedSignIn(provider) {
  const signIn = await awsSignInWithRedirect({ provider }).catch((err) => logger.error('Error during federated sign in', err));
  console.log('federated sign in', signIn);
  return signIn
}

async function forgotPassword(email) {
  return await resetPassword({ username: email });
}

async function forgotPasswordSubmit(email, code, newPassword) {
  try {
    await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
    return 'Password was changed successfully.';
  } catch (err) {
    logger.error('Error changing password', email, err);
    throw err;
  }
}

async function changePassword(oldPassword, newPassword) {
  try {
    await updatePassword({ oldPassword, newPassword });
    return 'Password was changed successfully.';
  } catch (err) {
    logger.error('Error changing password', err);
    throw err;
  }
}

function getIdToken() {
  return new Promise((resolve, reject) => {
    fetchAuthSession()
      .then((data) => {
        const idToken = data.getIdToken();
        resolve(idToken.jwtToken);
      })
      .catch((error) => {
        logger.error('Error getting id token', id, error);
        reject(Error('Not signed in.'));
      });
  });
}

function getCurrentUser() {
  return new Promise((resolve, reject) => {
    fetchAuthSession()
      .then((data) => {
        const idToken = data.getIdToken();
        const user = idToken.payload;
        resolve(user);
      })
      .catch((error) => {
        logger.error('Error getting current user', id, error);
        reject(Error('Not signed in.'));
      });
  });
}

export {
  signUp,
  signIn,
  confirmSignUp,
  resendConfirmationCode,
  signOut,
  federatedSignIn,
  forgotPassword,
  forgotPasswordSubmit,
  getIdToken,
  changePassword,
  getCurrentUser
};
